/**
 * A citation's rectangle, kept off the line below it.
 *
 * The detector pads every box it returns, and a prescription photographed at
 * a slight angle makes that worse: an upright rectangle around slanted text
 * is taller than the text, so it reaches into the next line. On the receita
 * this was written for (orçamento 833697) forty-nine of seventy-two
 * vertically adjacent boxes overlapped the box beneath them, and one covered
 * the whole of the following line.
 *
 * That turns a correct citation into a wrong-looking one. "Não orçado: Iodeto
 * de Potássio + Iodo Metalóide" cited the line naming those two ingredients,
 * and the rectangle drawn for it also covered "Tomar 1 DOSE pela manhã ou
 * primeira refeição" -- so the finding read as though it were pointing at the
 * posology.
 *
 * Drawing only, never the stored region: the box a detector produced is what
 * a person editing regions must see and drag, and what every later comparison
 * is anchored to. This shortens what is painted so a highlight sits on its
 * own line, and nothing else.
 */
export interface HighlightBox {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

/**
 * How much two boxes must share horizontally to be the same column of text.
 *
 * A quarter of the narrower one. Two boxes side by side in different columns
 * touch by a few percent when the page is skewed, and neither should shorten
 * the other.
 */
const SHARES_A_COLUMN = 0.25

/**
 * The most of a box this will take away.
 *
 * Only the spill. A box reaches about a quarter to a third of a line past its
 * own text, and taking that back is safe; anything more means the neighbour
 * is not the next line at all but a box overlapping or nested inside this one,
 * which the detector emits wherever it found the same text twice. Measured on
 * orçamento 833697: of eighty-three boxes, thirty-five would give back a
 * quarter or less, and twenty-seven have a neighbour starting higher than
 * that -- including the line naming Omega 3, whose neighbour starts thirty-
 * eight per cent down.
 *
 * A box in that second group is left exactly as detected. Painting half of
 * one, which an earlier version of this did, points at half a line of text
 * and is worse than the overlap it was fixing.
 */
const TRIMS_AT_MOST = 0.3

/**
 * `box`, shortened so it stops before the nearest box below it in the same
 * column. Unchanged when nothing is in its way, and unchanged when stopping
 * there would take more than the spill.
 */
export function trimToNextLine(box: HighlightBox, others: readonly HighlightBox[]): HighlightBox {
  const bottom = box.y + box.h
  let stop = bottom
  for (const other of others) {
    if (other === box) continue
    // Below this box's top, and starting before it ends: anything else is
    // either the same line or further down than the trim would reach.
    if (other.y <= box.y || other.y >= bottom) continue
    if (sharedWidth(box, other) < SHARES_A_COLUMN * Math.min(box.w, other.w)) continue
    stop = Math.min(stop, other.y)
  }
  const trimmed = stop - box.y
  if (trimmed >= box.h) return box
  return trimmed < box.h * (1 - TRIMS_AT_MOST) ? box : { ...box, h: trimmed }
}

function sharedWidth(a: HighlightBox, b: HighlightBox): number {
  return Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x))
}
